import {
  isPushAvailable,
  registerPush,
  getPushPermission,
  getPushToken,
  onPushToken,
  onPushError,
} from '@3pm-source/native-connector';
import { registerDeviceToken, deleteDeviceToken } from './api';
import { usePushStatus } from './push-status-store';

let tokenUnsub: (() => void) | null = null;
let errUnsub: (() => void) | null = null;
let lastToken: string | null = null;

function setStatus(status: 'idle' | 'active' | 'inactive') {
  usePushStatus.getState().setStatus(status);
}

function detectPlatform(): 'ios' | 'android' {
  const p = (globalThis as any).Capacitor?.getPlatform?.();
  return p === 'ios' ? 'ios' : 'android';
}

/**
 * Register for push and start syncing the device token to the server. Also
 * reports whether native push is actually working via the push-status store so
 * the web toast can fall back when it isn't. Safe to call on every authed mount.
 */
export async function initPushRegistration(): Promise<void> {
  // No shell push surface (web, or an older shell): native push can't work.
  if (!isPushAvailable()) {
    setStatus('inactive');
    return;
  }
  try {
    const perm = await getPushPermission();
    if (perm === 'denied') {
      setStatus('inactive'); // user declined — web toast should take over
      return;
    }
    await registerPush(); // requests permission if 'prompt', then issues a token
    const platform = detectPlatform();
    // onPushToken replays the last known token immediately, so we never miss it.
    tokenUnsub?.();
    // Annotated because the connector types these callbacks loosely, so
    // `noImplicitAny` rejects the bare parameter. `string` is what the value is
    // used as below (compared to `lastToken` and passed to registerDeviceToken).
    tokenUnsub = onPushToken((token: string) => {
      setStatus('active'); // a token means the OS will deliver push
      if (token === lastToken) return;
      lastToken = token;
      void registerDeviceToken(token, platform).catch(() => {});
    });
    errUnsub?.();
    // `unknown` rather than `Error`: the value is only logged, and a handler
    // taking a wider type stays assignable however the connector declares it.
    errUnsub = onPushError((err: unknown) => {
      setStatus('inactive'); // registration/token error — fall back to web toast
      console.error('push error', err);
    });
  } catch (err) {
    setStatus('inactive');
    console.error('initPushRegistration failed', err);
  }
}

/** Remove the current token server-side and stop syncing. No-op on web. */
export async function teardownPushRegistration(): Promise<void> {
  if (!isPushAvailable()) return;
  try {
    const token = lastToken ?? (await getPushToken());
    if (token) await deleteDeviceToken(token).catch(() => {});
  } finally {
    tokenUnsub?.();
    errUnsub?.();
    tokenUnsub = null;
    errUnsub = null;
    lastToken = null;
    setStatus('idle');
  }
}
